import { IListResponse } from '../api/types';

// ============ Schema Types ============

export interface IRulesetSchemaFieldChoice {
  value: string | number;
  label: string;
}

export interface IRulesetSchemaChoiceSource {
  type: 'resource';
  name: string;
}

export interface IRulesetSchemaOperator {
  name: string;
  label: string;
  value_type: 'none' | 'scalar' | 'list' | 'range' | 'regex' | 'regex_list';
  modifiers?: string[];
}

export interface IRulesetSchemaField {
  field_path?: string;
  name?: string;
  label: string;
  type: string;
  operators: IRulesetSchemaOperator[];
  choices?: IRulesetSchemaFieldChoice[];
  choices_source?: IRulesetSchemaChoiceSource;
  dynamic?: boolean;
}

export interface IRulesetSchemaActionParam {
  name: string;
  label?: string;
  type: string;
  required?: boolean;
  choices?: IRulesetSchemaFieldChoice[];
  choices_source?: IRulesetSchemaChoiceSource;
}

export type IRulesetSchemaActionParams =
  | IRulesetSchemaActionParam[]
  | Record<string, IRulesetSchemaActionParam>;

export interface IRulesetSchemaAction {
  type?: string;
  name?: string;
  label: string;
  params: IRulesetSchemaActionParams;
}

export interface IRulesetSchema {
  context_type?: string;
  fields: IRulesetSchemaField[];
  actions: IRulesetSchemaAction[];
}

// ============ Condition Types ============

export interface IConditionModifiers {
  case_insensitive?: boolean;
}

export interface IConditionLeaf {
  id?: string;
  type: 'leaf';
  field: string;
  operator: string;
  value?:
    | string
    | number
    | boolean
    | Array<string | number>
    | [number | null, number | null]
    | null;
  modifiers?: IConditionModifiers;
}

export interface IConditionAndOrGroup {
  id?: string;
  type: 'and' | 'or';
  conditions: IConditionNode[];
}

export interface IConditionNotGroup {
  id?: string;
  type: 'not';
  condition: IConditionNode;
}

export type IConditionGroup = IConditionAndOrGroup | IConditionNotGroup;

export type IConditionNode = IConditionLeaf | IConditionGroup;

// ============ Action Types ============

// Actions use a flat structure - params are directly on the object, not nested
export interface IRulesetAction {
  type: string;
  [key: string]: string | number | boolean | null | undefined;
}

// ============ Ruleset Entity Types ============

export interface IRuleset {
  id: string;
  url?: string;
  data_pool?: string;
  label: string;
  description: string;
  priority: number;
  is_active: boolean;
  rule_type?: string;
  conditions: IConditionNode;
  actions: IRulesetAction[];
  created_at: string;
  updated_at: string;
}

export interface IRulesetListParams {
  limit?: number;
  offset?: number;
  ordering?: string;
  is_active?: boolean;
  ilabel__contains?: string;
}

export interface IRulesetListResponse extends IListResponse {
  results: IRuleset[];
}

export interface IRulesetCreateParams {
  label: string;
  description?: string;
  priority?: number;
  is_active?: boolean;
  conditions: IConditionNode;
  actions: IRulesetAction[];
}

export interface IRulesetUpdateParams {
  label?: string;
  description?: string;
  priority?: number;
  is_active?: boolean;
  conditions?: IConditionNode;
  actions?: IRulesetAction[];
}

// ============ Builder Types ============

export enum RulesetBuilderMode {
  Create = 'create',
  Edit = 'edit',
  Copy = 'copy',
}

export const DEFAULT_RULESET_PRIORITY = 2;
export const COPY_LABEL_SUFFIX = 'copy';

// ============ Helper Types ============

export type ConditionGroupType = 'and' | 'or' | 'not';

export type ConditionValue =
  | string
  | number
  | boolean
  | Array<string | number>
  | [number | null, number | null]
  | null;

export interface IRulesetFormState {
  label: string;
  description: string;
  priority: number;
  isActive: boolean;
  conditions: IConditionNode;
  actions: IRulesetAction[];
}

export const createEmptyConditionLeaf = (id: string): IConditionLeaf => ({
  id,
  type: 'leaf',
  field: '',
  operator: '',
  value: null,
});

export const createEmptyConditionGroup = (
  id: string,
  groupType: ConditionGroupType = 'and',
  initialConditions: IConditionNode[] = []
): IConditionGroup =>
  groupType === 'not'
    ? {
        id,
        type: 'not',
        condition: initialConditions[0] || createEmptyConditionLeaf(`${id}-child`),
      }
    : {
        id,
        type: groupType,
        conditions: initialConditions,
      };

export const isConditionGroup = (node: IConditionNode): node is IConditionGroup => {
  return node.type === 'and' || node.type === 'or' || node.type === 'not';
};

export const isConditionNotGroup = (node: IConditionNode): node is IConditionNotGroup => {
  return node.type === 'not';
};

export const isConditionLeaf = (node: IConditionNode): node is IConditionLeaf => {
  return node.type === 'leaf';
};

export const getConditionChildren = (node: IConditionGroup): IConditionNode[] => {
  return node.type === 'not' ? [node.condition] : node.conditions;
};

/**
 * Label for a ruleset copied from `sourceLabel`, deduped against the labels
 * the caller has loaded. Best-effort: the server stays authoritative on any
 * uniqueness constraint.
 */
export const buildCopyLabel = (sourceLabel: string, existingLabels: string[]): string => {
  const base = `${sourceLabel} (${COPY_LABEL_SUFFIX})`;
  if (!existingLabels.includes(base)) return base;
  let n = 2;
  while (existingLabels.includes(`${sourceLabel} (${COPY_LABEL_SUFFIX} ${n})`)) n += 1;
  return `${sourceLabel} (${COPY_LABEL_SUFFIX} ${n})`;
};

/**
 * Returns a copy of the tree with every client-side id removed, so that
 * `normalizeConditionNode` assigns fresh ones instead of preserving the
 * source's identities.
 */
export const stripConditionIds = (node: IConditionNode): IConditionNode => {
  if (isConditionLeaf(node)) {
    const { id, ...rest } = node;
    return rest;
  }

  if (node.type === 'not') {
    const { id, ...rest } = node;
    return { ...rest, condition: stripConditionIds(node.condition) };
  }

  const { id, ...rest } = node;
  return { ...rest, conditions: node.conditions.map(stripConditionIds) };
};

export const withConditionId = <T extends IConditionLeaf | IConditionGroup>(
  node: T,
  id: string
): T => ({ ...node, id });

export const normalizeConditionNode = (
  node: IConditionNode,
  createId: () => string
): IConditionNode => {
  if (isConditionLeaf(node)) {
    return {
      ...node,
      id: node.id || createId(),
      modifiers: node.modifiers?.case_insensitive ? { case_insensitive: true } : undefined,
    };
  }

  if (node.type === 'not') {
    return {
      ...node,
      id: node.id || createId(),
      condition: normalizeConditionNode(node.condition, createId),
    };
  }

  return {
    ...node,
    id: node.id || createId(),
    conditions: node.conditions.map((child) => normalizeConditionNode(child, createId)),
  };
};

export const normalizeRootConditionNode = (
  node: IConditionNode,
  createId: () => string
): IConditionNode => {
  const normalizedNode = normalizeConditionNode(node, createId);

  if (isConditionGroup(normalizedNode)) {
    return normalizedNode;
  }

  return {
    id: createId(),
    type: 'and',
    conditions: [normalizedNode],
  };
};

export const serializeConditionNode = (
  node: IConditionNode,
  isRoot: boolean = true
): IConditionNode => {
  if (isConditionLeaf(node)) {
    return {
      type: 'leaf',
      field: node.field,
      operator: node.operator,
      ...(node.value !== undefined ? { value: node.value } : {}),
      ...(node.modifiers?.case_insensitive ? { modifiers: { case_insensitive: true } } : {}),
    };
  }

  if (node.type === 'not') {
    return {
      type: 'not',
      condition: serializeConditionNode(node.condition, false),
    };
  }

  if (isRoot && node.conditions.length === 1) {
    return serializeConditionNode(node.conditions[0], false);
  }

  return {
    type: node.type,
    conditions: node.conditions.map((child) => serializeConditionNode(child, false)),
  };
};

export interface IRulesetDryRunRequest {
  document?: string;
  context?: Record<string, unknown>;
}

export interface IRulesetDryRunTrace {
  node_type?: string;
  matched: boolean;
  elapsed_ms: number;
  field?: string;
  operator?: string;
  expected?: unknown;
  actual?: unknown;
  error?: string | null;
  children?: IRulesetDryRunTrace[];
}

export interface IRulesetDryRunResponse {
  matched: boolean;
  actions: IRulesetAction[];
  trace: IRulesetDryRunTrace;
  context: Record<string, unknown>;
}

// ============ Action Param Applicability ============

export const SET_STATUS_ACTION_TYPE = 'set_status';
export const EXCLUSION_REASON_PARAM = 'reason';
export const REASON_BEARING_STATUS = 'EXCLUDED';

/**
 * Whether a schema param actually applies to the action as configured.
 *
 * The rules schema advertises `status` and `reason` on `set_status`
 * unconditionally, but `reason` is EXCLUDED's companion: the API writes it to
 * `DocumentData.exclusion_reason` and discards it for any other target (see
 * `_exec_set_status` in glynt-api `documents/routing/actions.py`). Which params
 * pair with which status is therefore the builder's call. If another settable
 * status ever gains a companion param, teach it here — the backend lists live
 * in `SETTABLE_STATUSES` / `SETTABLE_EXCLUSION_REASONS`.
 */
export const isActionParamApplicable = (action: IRulesetAction, paramName: string): boolean => {
  if (action.type !== SET_STATUS_ACTION_TYPE || paramName !== EXCLUSION_REASON_PARAM) {
    return true;
  }
  return action.status === REASON_BEARING_STATUS;
};

/**
 * Drops params that no longer apply. A `reason` left behind by switching status
 * away from EXCLUDED would otherwise reach the JSON preview and the saved
 * ruleset. Returns the input untouched when there is nothing to prune, so it is
 * safe to call on every render.
 */
export const pruneInapplicableActionParams = (actions: IRulesetAction[]): IRulesetAction[] => {
  let changed = false;

  const pruned = actions.map((action) => {
    const kept = Object.entries(action).filter(
      ([key]) => key === 'type' || isActionParamApplicable(action, key)
    );
    if (kept.length === Object.keys(action).length) {
      return action;
    }
    changed = true;
    return Object.fromEntries(kept) as IRulesetAction;
  });

  return changed ? pruned : actions;
};
